import { useContext, createContext, useState, useEffect, useRef } from "react";

export const MeetingAppContext = createContext();

export const useMeetingAppContext = () => useContext(MeetingAppContext);

export const MeetingAppProvider = ({ children }) => {
  const [selectedMic, setSelectedMic] = useState({ id: null, label: null });
  const [selectedWebcam, setSelectedWebcam] = useState({
    id: null,
    label: null,
  });
  const [selectedSpeaker, setSelectedSpeaker] = useState({
    id: null,
    label: null,
  });
  const [isCameraPermissionAllowed, setIsCameraPermissionAllowed] =
    useState(null);
  const [isMicrophonePermissionAllowed, setIsMicrophonePermissionAllowed] =
    useState(null);
  const [raisedHandsParticipants, setRaisedHandsParticipants] = useState([]);
  const [sideBarMode, setSideBarMode] = useState("CHAT");
  // const [pipMode, setPipMode] = useState(false);

  // Bot message queue state
  const [pendingBotMessages, setPendingBotMessages] = useState([]);
  const [processedBotMessages, setProcessedBotMessages] = useState([]);

  const useRaisedHandParticipants = () => {
    const raisedHandsParticipantsRef = useRef();

    const participantRaisedHand = (participantId) => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const newItem = { participantId, raisedHandOn: new Date().getTime() };

      const participantFound = raisedHandsParticipants.findIndex(
        ({ participantId: pID }) => pID === participantId
      );

      if (participantFound === -1) {
        raisedHandsParticipants.push(newItem);
      } else {
        raisedHandsParticipants[participantFound] = newItem;
      }

      setRaisedHandsParticipants(raisedHandsParticipants);
    };

    useEffect(() => {
      raisedHandsParticipantsRef.current = raisedHandsParticipants;
    }, [raisedHandsParticipants]);

    const _handleRemoveOld = () => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const now = new Date().getTime();

      const persisted = raisedHandsParticipants.filter(({ raisedHandOn }) => {
        return parseInt(raisedHandOn) + 15000 > parseInt(now);
      });

      if (raisedHandsParticipants.length !== persisted.length) {
        setRaisedHandsParticipants(persisted);
      }
    };

    useEffect(() => {
      const interval = setInterval(_handleRemoveOld, 1000);

      return () => {
        clearInterval(interval);
      };
    }, []);

    return { participantRaisedHand };
  };

  // New state variable for AI assistants
  const [aiAssistants, setAiAssistants] = useState([
    {
      participantId: "ai-assistant-1",
      displayName: "Anushka",
      isBot: true,
    },
  ]);

  // Add a function to add AI assistants
  const addAiAssistant = (assistantDetails) => {
    setAiAssistants((prev) => [
      ...prev,
      {
        participantId: `ai-assistant-${prev.length + 1}`,
        displayName: assistantDetails.name || `Anushka ${prev.length + 1}`,
        isBot: true,
        ...assistantDetails,
      },
    ]);
  };

  // Remove an AI assistant
  const removeAiAssistant = (participantId) => {
    setAiAssistants((prev) =>
      prev.filter((assistant) => assistant.participantId !== participantId)
    );
  };

  // Bot message queue functions
  const queueBotMessage = (messageData) => {
    const queuedMessage = {
      ...messageData,
      queuedAt: Date.now(),
      id: `bot-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    setPendingBotMessages((prev) => [...prev, queuedMessage]);
  };

  const processPendingBotMessage = () => {
    if (pendingBotMessages.length > 0) {
      const messageToProcess = pendingBotMessages[0];



      // Move message from pending to processed
      setPendingBotMessages((prev) => prev.slice(1));
      setProcessedBotMessages((prev) => [...prev, messageToProcess]);

      // Emit event for ChatPanel to pick up
      window.dispatchEvent(
        new CustomEvent("display-bot-message", {
          detail: messageToProcess,
        })
      );

      return messageToProcess;
    }
    return null;
  };

  const clearOldBotMessages = () => {
    const now = Date.now();
    const maxAge = 30000; // 30 seconds

    // Clear old pending messages
    setPendingBotMessages((prev) =>
      prev.filter((msg) => now - msg.queuedAt < maxAge)
    );

    // Keep only recent processed messages
    setProcessedBotMessages((prev) =>
      prev.filter((msg) => now - msg.queuedAt < maxAge)
    );
  };

  // Clean up old messages periodically
  useEffect(() => {
    const cleanup = setInterval(clearOldBotMessages, 10000); // Every 10 seconds
    return () => clearInterval(cleanup);
  }, []);

  return (
    <MeetingAppContext.Provider
      value={{
        // states
        raisedHandsParticipants,
        selectedMic,
        selectedWebcam,
        selectedSpeaker,
        sideBarMode,
        // pipMode,
        isCameraPermissionAllowed,
        isMicrophonePermissionAllowed,
        pendingBotMessages,
        processedBotMessages,

        // setters
        setRaisedHandsParticipants,
        setSelectedMic,
        setSelectedWebcam,
        setSelectedSpeaker,
        setSideBarMode,
        // setPipMode,
        useRaisedHandParticipants,
        setIsCameraPermissionAllowed,
        setIsMicrophonePermissionAllowed,

        // Add AI assistants and related methods
        aiAssistants,
        addAiAssistant,
        removeAiAssistant,

        // Bot message queue methods
        queueBotMessage,
        processPendingBotMessage,
        clearOldBotMessages,
      }}
    >
      {children}
    </MeetingAppContext.Provider>
  );
};
